import { Link } from "react-router-dom";
import { Seo } from "@/components/site/Seo";

/**
 * Shared frame for every legal page (Terms, Privacy, Refund, KYC/AML).
 *
 * Renders a consistent header (title + "Last updated" line), the page
 * body slot, and a cross-link row so users can hop between all four
 * documents without going through the footer. Also emits BreadcrumbList
 * JSON-LD via the `<Seo>` component for search-engine hygiene.
 */
const LEGAL_LINKS = [
  { path: "/terms-of-service",   label: "Terms of Service" },
  { path: "/privacy-policy",     label: "Privacy Policy" },
  { path: "/refund-policy",      label: "Refund & Withdrawal Policy" },
  { path: "/kyc-aml-policy",     label: "KYC & AML Policy" },
];

export const LegalLayout = ({
  title,
  lastUpdated,
  metaTitle,
  metaDescription,
  path,
  children,
}) => {
  const otherLinks = LEGAL_LINKS.filter((l) => l.path !== path);
  return (
    <div data-testid={`legal-${path.replace(/\//g, "")}-page`} className="bg-white">
      <Seo
        title={metaTitle || title}
        description={metaDescription}
        keywords={`${title.toLowerCase()}, danny clans media, legal, dc dashboard, hong kong`}
        path={path}
        breadcrumbs={[
          { name: "Home",  url: "/" },
          { name: "Legal", url: path },
          { name: title,   url: path },
        ]}
      />

      <header className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto w-full max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Legal</p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {lastUpdated}</p>
          <p className="mt-3 text-sm text-slate-600">Danny Clans Media Limited — Hong Kong SAR incorporated.</p>
        </div>
      </header>

      <article
        className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 lg:px-8
          [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900
          [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900
          [&_p]:mt-3 [&_p]:leading-relaxed [&_p]:text-slate-700
          [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:text-slate-700
          [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 [&_ol]:text-slate-700
          [&_a]:text-blue-700 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-blue-800
          [&_strong]:text-slate-900"
      >
        {children}
      </article>

      <nav aria-label="Related legal documents" className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-6 text-sm sm:px-6 lg:px-8">
          <span className="font-semibold text-slate-900">Related:</span>
          {otherLinks.map((l) => (
            <Link key={l.path} to={l.path} className="text-blue-700 hover:text-blue-800 hover:underline underline-offset-2">
              {l.label}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default LegalLayout;
